import { useState } from "react";
import { Link } from "react-router";
import { ArrowRight, ClipboardList } from "lucide-react";
import { Card, Skeleton } from "@bookdental/ui";
import { STATUS_PHASES, formatDate } from "@/components/prosthesis/prosthesis-meta";
import {
  flattenServiceRows,
  serviceLabel,
  type IncomingOrderRow,
  type ServiceRow,
} from "@/components/production/production-types";
import { ProductionOrderDetailDialog } from "@/components/production/ProductionOrderDetailDialog";
import { ProductionServiceDetailDialog } from "@/components/production/ProductionServiceDetailDialog";

const MAX_VISIBLE = 8;

type Props = {
  orders: IncomingOrderRow[];
  loading: boolean;
};

/** Início do colaborador sem production.viewAll — em vez do resumo por prazo (que pressupõe
 *  visão do pipeline inteiro), mostra direto os trabalhos delegados a ele. O servidor já filtra
 *  `orders` por assignedToUserId (ver scopeOrdersToAssignee em prosthesis.ts), então aqui é só
 *  listar o que sobrou. */
export function MyProductionList({ orders, loading }: Props) {
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailServiceId, setDetailServiceId] = useState<string | null>(null);

  const allRows = flattenServiceRows(orders).filter((r) => r.service);
  const detailOrder = orders.find((o) => o.id === detailOrderId) ?? null;
  const detailRow = allRows.find((r) => r.service?.id === detailServiceId) ?? null;

  const openOrder = (order: IncomingOrderRow) => {
    setDetailServiceId(null);
    setDetailOrderId(order.id);
  };
  const openService = (row: ServiceRow) => setDetailServiceId(row.service?.id ?? null);

  const sorted = [...allRows].sort(
    (a, b) =>
      STATUS_PHASES.findIndex((p) => p.statuses.includes(a.service!.status)) -
      STATUS_PHASES.findIndex((p) => p.statuses.includes(b.service!.status)),
  );
  const visible = sorted.slice(0, MAX_VISIBLE);

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary">
            <ClipboardList className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">Meus trabalhos</h2>
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">Serviços delegados a você</p>
          </div>
        </div>
        <Link
          to="/lab/producao"
          className="-mx-2 inline-flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 md:mx-0 md:h-auto md:px-0 dark:text-primary"
        >
          Ver produção completa
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
            Nenhum trabalho delegado a você no momento.
          </p>
        ) : (
          <div className="space-y-2">
            {visible.map((row) => (
              <button
                key={row.service!.id}
                type="button"
                onClick={() => openService(row)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white p-3 text-left transition-all hover:border-zinc-300 hover:shadow-sm dark:border-border dark:bg-card"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-zinc-800 dark:text-foreground">
                    {row.order.patientName}
                  </p>
                  <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
                    {row.order.organization.isPersonal ? "" : `${row.order.organization.name} · `}
                    {serviceLabel(row.service)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  {row.service!.productionStage && (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{
                        backgroundColor: `${row.service!.productionStage.colorHex ?? "#2457ff"}1a`,
                        color: row.service!.productionStage.colorHex ?? "#2457ff",
                      }}
                    >
                      {row.service!.productionStage.label}
                    </span>
                  )}
                  <p className="mt-0.5 text-xs text-zinc-400 dark:text-muted-foreground">
                    Prazo: {formatDate(row.order.deadline)}
                  </p>
                </div>
              </button>
            ))}
            {sorted.length > MAX_VISIBLE && (
              <Link
                to="/lab/producao"
                className="block px-1 py-1 text-xs font-medium text-brand-700 hover:underline dark:text-primary"
              >
                +{sorted.length - MAX_VISIBLE} trabalho(s)
              </Link>
            )}
          </div>
        )}
      </div>

      <ProductionServiceDetailDialog row={detailRow} onClose={() => setDetailServiceId(null)} onOpenOrder={openOrder} />
      <ProductionOrderDetailDialog order={detailOrder} onClose={() => setDetailOrderId(null)} />
    </Card>
  );
}
